import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { organisationSchema, SITE_URL } from "../lib/seo";
import { gaEvent, CONVERSION_EVENTS } from "../lib/analytics";

const PDF_PATH = "/hospitality-audit-guide.pdf";

const HIGHLIGHTS = [
    "Eight operational checks covering labour, standards, cadence, food and beverage, revenue, people, reporting and owner visibility.",
    "Four practical actions per check that you can run yourself this week, without outside help.",
    "Written for hotel owners, general managers and operations directors, not for marketing departments.",
    "Used as the foundation for the operational audit Optimum Hospitality Solutions delivers across the UK.",
];

export default function GuidePage() {
    useSEO({
        title: "The Hospitality Audit Guide — Free Download | Optimum Hospitality Solutions",
        description: "A free 10-page operational audit guide for UK hotel owners, general managers and operations directors. Eight checks covering labour, standards, cadence and performance. Written by Sidney Adams.",
        path: "/hospitality-audit-guide",
    });

    const [form, setForm] = useState({ name: "", email: "", organisation: "", role: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Please share your name and email.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/guide-downloads", form);
            gaEvent(CONVERSION_EVENTS.GUIDE_DOWNLOAD, { source: "lead_magnet_form" });
            setDone(true);
            toast.success("Thanks. The guide is ready to download.");
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Hospitality Audit Guide", item: `${SITE_URL}/hospitality-audit-guide` },
        ],
    };

    return (
        <div data-testid="guide-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Free Resource</span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                            The Hospitality <em className="italic text-brand-champagne not-italic font-display">Audit Guide</em>.
                        </h1>
                        <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                            Eight operational checks to take into your next operations meeting. Built by an operator, for hotel owners, general managers and operations directors across the UK.
                        </p>

                        <ul className="mt-10 space-y-4 max-w-2xl">
                            {HIGHLIGHTS.map((h) => (
                                <li key={h} className="flex gap-3 text-brand-ink/80 leading-relaxed">
                                    <CheckCircle2 className="text-brand-champagne shrink-0 mt-0.5" size={18} strokeWidth={1.5} />
                                    <span>{h}</span>
                                </li>
                            ))}
                        </ul>

                        <p className="mt-10 text-sm text-brand-ink/55">
                            PDF · 10 pages · A4 · ~150 KB · No tracking cookies.
                        </p>
                    </div>

                    <aside className="lg:col-span-5">
                        <div className="bg-brand-navy text-brand-stone p-8 lg:p-10 border border-brand-champagne/30" data-testid="guide-form-panel">
                            {!done ? (
                                <>
                                    <div className="flex items-center gap-3">
                                        <FileText className="text-brand-champagne" size={22} strokeWidth={1.25} />
                                        <div className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Download</div>
                                    </div>
                                    <h2 className="font-display text-2xl mt-4 text-brand-stone leading-snug">
                                        Get the guide.
                                    </h2>
                                    <p className="mt-3 text-sm text-brand-stone/75 leading-relaxed">
                                        Share your details and the guide will be available immediately. We will only contact you if you ask us to.
                                    </p>

                                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="guide-form">
                                        <div>
                                            <Label htmlFor="g-name" className="text-xs uppercase tracking-[0.18em] text-brand-stone/65">Name</Label>
                                            <Input
                                                id="g-name"
                                                data-testid="guide-input-name"
                                                value={form.name}
                                                onChange={(e) => set("name", e.target.value)}
                                                placeholder="Your name"
                                                className="mt-2 bg-brand-stone/10 border-brand-stone/30 text-brand-stone placeholder:text-brand-stone/40 focus:border-brand-champagne"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="g-email" className="text-xs uppercase tracking-[0.18em] text-brand-stone/65">Email</Label>
                                            <Input
                                                id="g-email"
                                                data-testid="guide-input-email"
                                                type="email"
                                                value={form.email}
                                                onChange={(e) => set("email", e.target.value)}
                                                placeholder="you@property.com"
                                                className="mt-2 bg-brand-stone/10 border-brand-stone/30 text-brand-stone placeholder:text-brand-stone/40 focus:border-brand-champagne"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="g-org" className="text-xs uppercase tracking-[0.18em] text-brand-stone/65">Property or organisation <span className="text-brand-stone/40 normal-case tracking-normal">(optional)</span></Label>
                                            <Input
                                                id="g-org"
                                                data-testid="guide-input-organisation"
                                                value={form.organisation}
                                                onChange={(e) => set("organisation", e.target.value)}
                                                placeholder="Property or group"
                                                className="mt-2 bg-brand-stone/10 border-brand-stone/30 text-brand-stone placeholder:text-brand-stone/40 focus:border-brand-champagne"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="g-role" className="text-xs uppercase tracking-[0.18em] text-brand-stone/65">Role <span className="text-brand-stone/40 normal-case tracking-normal">(optional)</span></Label>
                                            <Input
                                                id="g-role"
                                                data-testid="guide-input-role"
                                                value={form.role}
                                                onChange={(e) => set("role", e.target.value)}
                                                placeholder="Owner, General Manager, Operations Director…"
                                                className="mt-2 bg-brand-stone/10 border-brand-stone/30 text-brand-stone placeholder:text-brand-stone/40 focus:border-brand-champagne"
                                            />
                                        </div>

                                        <Button
                                            type="submit"
                                            data-testid="guide-submit"
                                            disabled={submitting}
                                            className="w-full bg-brand-champagne text-brand-navy hover:bg-white tracking-wide rounded-none py-6 font-medium"
                                        >
                                            {submitting ? (
                                                <><Loader2 className="animate-spin mr-2" size={16} />Sending…</>
                                            ) : (
                                                <>Get the guide <ArrowUpRight size={16} className="ml-2" /></>
                                            )}
                                        </Button>

                                        <p className="text-[11px] text-brand-stone/55 leading-relaxed">
                                            By submitting you agree to our <Link to="/privacy-policy" className="underline">Privacy Policy</Link>.
                                        </p>
                                    </form>
                                </>
                            ) : (
                                <div data-testid="guide-success">
                                    <CheckCircle2 className="text-brand-champagne" size={32} strokeWidth={1.25} />
                                    <h2 className="font-display text-2xl mt-5 text-brand-stone leading-snug">
                                        Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}.
                                    </h2>
                                    <p className="mt-3 text-sm text-brand-stone/75 leading-relaxed">
                                        The guide is ready below. A copy has also been emailed to {form.email} for your records.
                                    </p>
                                    <a
                                        href={PDF_PATH}
                                        download
                                        data-testid="guide-download-link"
                                        className="mt-8 inline-flex items-center justify-center gap-2 w-full bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white py-3.5 font-medium"
                                    >
                                        <Download size={16} />
                                        Download the guide (PDF)
                                    </a>
                                    <Link
                                        to="/booking"
                                        className="mt-4 inline-flex items-center justify-center gap-2 w-full border border-brand-champagne text-brand-stone text-sm tracking-wide hover:bg-brand-champagne hover:text-brand-navy py-3.5"
                                    >
                                        Book a Consultation
                                        <ArrowUpRight size={16} />
                                    </Link>
                                </div>
                            )}
                        </div>
                    </aside>
                </div>
            </section>

            <section className="bg-brand-navy">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl text-brand-stone leading-tight">
                            Want a second pair of eyes on your operation?
                        </h2>
                        <p className="mt-4 text-brand-stone/75 max-w-2xl text-lg">
                            An independent operational audit typically takes three weeks from kick off to written report.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to="/booking" className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white">
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
